import {
  WhatsAppAccount,
  WhatsAppConnection,
  AIAgentConfig,
  CustomerContact,
  KnowledgeItem,
  ExtractedKnowledgeItem,
  LinkedPresentationItem,
  MasterPresentationContent,
  BroadcastCampaign,
  DashboardMetrics,
  ChatMessage,
  Appointment,
  AppointmentSlot,
  BusinessSchedule,
  BusinessHoliday,
  AppointmentConfig,
  OperationsData,
  OperationOrder,
  OperationDelivery,
  OperationReport,
  OperationType,
  OrderStatus,
  DeliveryStatus,
  ReportStatus,
  ReportPriority,
  ReportType,
  CampaignEstimate
} from '../types';
import { API_BASE, getBackendOrigin } from '../config/api';
import { io, Socket } from 'socket.io-client';

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem('zeyilo_auth_token');
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem('zeyilo_refresh_token');
  } catch {
    return null;
  }
}

export function saveAuthTokens(token?: string | null, refreshToken?: string | null): void {
  try {
    if (token) localStorage.setItem('zeyilo_auth_token', token);
    if (refreshToken) localStorage.setItem('zeyilo_refresh_token', refreshToken);
  } catch {}
}

export function clearAuthTokens(): void {
  try {
    localStorage.removeItem('zeyilo_auth_token');
    localStorage.removeItem('zeyilo_refresh_token');
  } catch {}
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  try {
    let dfp = localStorage.getItem('zeyilo_device_fp');
    if (!dfp) {
      dfp = `dfp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 10)}`;
      localStorage.setItem('zeyilo_device_fp', dfp);
    }
    headers['x-device-fingerprint'] = dfp;
  } catch {}
  return headers;
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  try {
    const rf = getRefreshToken();
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(rf ? { Authorization: `Bearer ${rf}` } : {})
      },
      body: JSON.stringify({ refreshToken: rf || undefined })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.success && data.token) {
      saveAuthTokens(data.token, data.refreshToken);
      return data.token;
    }
  } catch (err) {
    console.warn('[Auth Refresh] Erreur lors du rafraîchissement transparent:', err);
  }
  return null;
}

async function fetchJson<T>(url: string, options?: RequestInit, isRetry = false): Promise<T> {
  const { headers: customHeaders, ...restOptions } = options || {};
  const res = await fetch(url, {
    ...restOptions,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...getAuthHeaders(),
      ...(customHeaders || {})
    }
  });

  // Si 401 et qu'il ne s'agit pas déjà d'une tentative ou d'une route d'authentification publique
  if (
    res.status === 401 &&
    !isRetry &&
    !url.includes('/auth/login') &&
    !url.includes('/auth/register') &&
    !url.includes('/auth/refresh') &&
    !url.includes('/auth/2fa/verify')
  ) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      return fetchJson<T>(url, options, true);
    }
  }

  // Lire le body une seule fois de façon robuste (gère 204 / vide / HTML)
  const text = await res.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return {} as any; } })() : ({} as any);

  if (!res.ok) {
    const err = new Error((data as any).error || (data as any).message || `Erreur HTTP ${res.status} ${text?.slice(0,120) || ''}`) as any;
    err.status = res.status;
    err.data = data; // Attache la réponse JSON complète (code, error, planId…) pour les callers
    throw err;
  }

  // Si 204 ou vide, retourne objet vide typé
  if (!text) return {} as T;
  return data as T;
}

export const api = {
  // --- COMPTES ---
  accounts: {
    getAll: async (): Promise<WhatsAppAccount[]> => {
      const res = await fetchJson<{ success: boolean; data: WhatsAppAccount[] }>(`${API_BASE}/accounts`);
      return res.data;
    },
    create: async (accountData: Partial<WhatsAppAccount> & { name: string; phoneNumber?: string }): Promise<WhatsAppAccount> => {
      const res = await fetchJson<{ success: boolean; data: WhatsAppAccount }>(`${API_BASE}/accounts`, {
        method: 'POST',
        body: JSON.stringify(accountData)
      });
      return res.data;
    },
    update: async (id: string, data: { name?: string; phoneNumber?: string; isAiActive?: boolean; resetHistory?: boolean }): Promise<void> => {
      await fetchJson(`${API_BASE}/accounts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },
    delete: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/accounts/${id}`, { method: 'DELETE' });
    },
    getStatus: async (id: string): Promise<WhatsAppConnection> => {
      const res = await fetchJson<{ success: boolean; data: WhatsAppConnection }>(`${API_BASE}/accounts/${id}/status`);
      return res.data;
    }
  },

  // --- WHATSAPP & QR ---
  whatsapp: {
    connect: async (params: { sessionId?: string; phoneNumber?: string; method?: 'qr' | 'pair-code'; tier?: string }) => {
      return fetchJson<{ success: boolean; method: string; code?: string; qr?: string; hasQR?: boolean; status?: string; pairingCode?: string; ownerPhone?: string; ownerName?: string }>(
        `${API_BASE}/whatsapp/connect`,
        {
          method: 'POST',
          body: JSON.stringify(params)
        }
      );
    },
    disconnect: async (sessionId: string = 'default') => {
      return fetchJson<{ success: boolean; status: string }>(`${API_BASE}/whatsapp/disconnect`, {
        method: 'POST',
        body: JSON.stringify({ sessionId })
      });
    },
    reconnect: async (sessionId: string = 'default', options: { force?: boolean } = {}) => {
      return fetchJson<{ success: boolean; hasAuth?: boolean; status: string; message?: string; isConflict?: boolean; code?: string; conflictingWith?: string; error?: string }>(
        `${API_BASE}/whatsapp/reconnect`,
        {
          method: 'POST',
          body: JSON.stringify({ sessionId, force: !!options.force })
        }
      );
    },
    logout: async (sessionId: string = 'default') => {
      return fetchJson<{ success: boolean }>(`${API_BASE}/whatsapp/logout`, {
        method: 'POST',
        body: JSON.stringify({ sessionId })
      });
    },
    syncContacts: async (sessionId: string = 'default') => {
      return fetchJson<{ success: boolean; proxy?: any }>(`${API_BASE}/whatsapp/sync-contacts`, {
        method: 'POST',
        body: JSON.stringify({ sessionId })
      });
    },
    checkAuth: async (params: { sessionId?: string; phoneNumber?: string }) => {
      const q = new URLSearchParams();
      if (params.sessionId) q.set('sessionId', params.sessionId);
      if (params.phoneNumber) q.set('phoneNumber', params.phoneNumber);
      return fetchJson<{ success: boolean; hasAuth: boolean; canAutoConnect: boolean; dbStatus: string | null; proxyStatus: string | null; message: string; isConflict?: boolean; canClaimTransfer?: boolean; ownerPhone?: string; conflictingWith?: string }>( 
        `${API_BASE}/whatsapp/check-auth?${q.toString()}`
      );
    },
    claimConflictTransfer: async (sessionId: string) => {
      return fetchJson<{ success: boolean; message: string }>(
        `${API_BASE}/whatsapp/claim-conflict-transfer`,
        { method: 'POST', body: JSON.stringify({ sessionId }) }
      );
    },
    rejectConflictTransfer: async (sessionId: string) => {
      return fetchJson<{ success: boolean; message: string }>(
        `${API_BASE}/whatsapp/reject-conflict-transfer`,
        { method: 'POST', body: JSON.stringify({ sessionId }) }
      );
    },
    getQR: async (sessionId: string = 'default') => {
      return fetchJson<{ success: boolean; qr: string | null; status: string | null; hasQR: boolean; pairingCode: string | null }>(
        `${API_BASE}/whatsapp/qr/${encodeURIComponent(sessionId)}`
      );
    },
    createRemoteLink: async (params: { accountId: string; accountName?: string; clientName?: string; phoneNumber?: string; allowGuestRename?: boolean; durationHours?: number }) => {
      return fetchJson<{
        success: boolean;
        link: {
          id: string;
          token: string;
          url: string;
          accountId: string;
          accountName?: string;
          clientName?: string;
          allowGuestRename?: boolean;
          phoneNumber?: string;
          expiresAt: string;
          status: string;
        };
      }>(`${API_BASE}/whatsapp/remote-links`, {
        method: 'POST',
        body: JSON.stringify(params)
      });
    },
    getRemoteLink: async (accountId: string) => {
      return fetchJson<{
        success: boolean;
        link: {
          id: string;
          token: string | null;
          url: string | null;
          isHashed?: boolean;
          accountId: string;
          accountName?: string;
          clientName?: string;
          allowGuestRename?: boolean;
          phoneNumber?: string;
          expiresAt: string;
          status: string;
          usedAt?: string;
        } | null;
      }>(`${API_BASE}/whatsapp/remote-links/${encodeURIComponent(accountId)}`);
    },
    revokeRemoteLink: async (id: string) => {
      return fetchJson<{ success: boolean }>(`${API_BASE}/whatsapp/remote-links/${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
    }
  },

  // --- PORTAIL PUBLIC INVITÉ D'ASSOCIATION À DISTANCE ---
  remoteClient: {
    getInfo: async (token: string) => {
      return fetchJson<{
        success: boolean;
        isAlreadyConnected?: boolean;
        code?: string;
        message?: string;
        data?: {
          clientName: string;
          phoneNumber: string;
          accountName: string;
          allowGuestRename?: boolean;
          businessName: string;
          expiresAt: string;
        };
      }>(`${API_BASE}/remote-connect/${encodeURIComponent(token)}`);
    },
    connect: async (token: string, params: { method: 'pair-code' | 'qr'; phoneNumber?: string; accountName?: string }) => {
      return fetchJson<{
        success: boolean;
        method: string;
        code?: string;
        error?: string;
        data?: any;
      }>(`${API_BASE}/remote-connect/${encodeURIComponent(token)}/connect`, {
        method: 'POST',
        body: JSON.stringify(params)
      });
    },
    getQR: async (token: string) => {
      return fetchJson<{
        success: boolean;
        qr: string | null;
        status: string | null;
        hasQR: boolean;
        pairingCode: string | null;
      }>(`${API_BASE}/remote-connect/${encodeURIComponent(token)}/qr`);
    },
    getStatus: async (token: string) => {
      return fetchJson<{
        success: boolean;
        status: string;
        message?: string;
      }>(`${API_BASE}/remote-connect/${encodeURIComponent(token)}/status`);
    }
  },

  // --- CONTACTS & MESSAGES ---
  contacts: {
    getAll: async (params?: {
      accountId?: string;
      search?: string;
      status?: string;
      limit?: number;
      offset?: number;
      hasMessagesOnly?: boolean;
      signal?: AbortSignal;
    }): Promise<CustomerContact[] & { total: number; hasMore: boolean }> => {
      const query = new URLSearchParams();
      if (params?.accountId) query.set('accountId', params.accountId);
      if (params?.search) query.set('search', params.search);
      if (params?.status) query.set('status', params.status);
      if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
      if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
      if (params?.hasMessagesOnly) query.set('hasMessagesOnly', 'true');

      const res = await fetchJson<{ success: boolean; data: CustomerContact[]; total?: number; hasMore?: boolean }>(
        `${API_BASE}/contacts?${query.toString()}`,
        params?.signal ? { signal: params.signal } : undefined
      );
      const rawList = Array.isArray(res?.data) ? res.data : [];
      const mapped = rawList.map((c) => ({
        ...c,
        name: c.name || '',
        phoneNumber: c.phoneNumber || '',
        lastMessage: c.lastMessage || '',
        tags: Array.isArray(c.tags) ? c.tags : []
      }));
      (mapped as any).total = typeof res?.total === 'number' ? res.total : mapped.length;
      (mapped as any).hasMore = Boolean(res?.hasMore);
      return mapped as any;
    },
    getMessages: async (contactId: string, params?: { offset?: number; limit?: number; before?: string; signal?: AbortSignal }): Promise<{ data: ChatMessage[]; total: number; hasMore: boolean }> => {
      const query = new URLSearchParams();
      if (params?.offset) query.set('offset', String(params.offset));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.before) query.set('before', params.before);
      const qs = query.toString() ? `?${query.toString()}` : '';
      const res = await fetchJson<{ success: boolean; data: ChatMessage[]; total: number; hasMore: boolean }>(
        `${API_BASE}/contacts/${encodeURIComponent(contactId)}/messages${qs}`,
        params?.signal ? { signal: params.signal } : undefined
      );
      return { data: res.data, total: res.total || 0, hasMore: res.hasMore || false };
    },
    getPresence: async (contactId: string): Promise<{
      success: boolean;
      presence: { phone: string; isOnline: boolean; status: string; lastSeen: number | null };
    }> => {
      return fetchJson<{
        success: boolean;
        presence: { phone: string; isOnline: boolean; status: string; lastSeen: number | null };
      }>(`${API_BASE}/contacts/${encodeURIComponent(contactId)}/presence`);
    },
    sendPresence: async (
      contactId: string,
      presence: 'composing' | 'paused' | 'recording' | 'available' = 'composing'
    ): Promise<boolean> => {
      try {
        await fetchJson<{ success: boolean }>(
          `${API_BASE}/contacts/${encodeURIComponent(contactId)}/presence`,
          {
            method: 'POST',
            body: JSON.stringify({ presence })
          }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    sendMessage: async (
      contactId: string,
      text: string,
      sender: 'agent' | 'bot' = 'agent',
      mediaOptions?: {
        mediaBase64?: string;
        mediaType?: string;
        mediaMimeType?: string;
        mediaFileName?: string;
        isVoiceNote?: boolean;
        tempId?: string;
      },
      tempId?: string
    ) => {
      const effectiveTempId = tempId || mediaOptions?.tempId;
      return fetchJson<{ success: boolean; data: ChatMessage & { tempId?: string }; contact: CustomerContact }>(
        `${API_BASE}/messages/send`,
        {
          method: 'POST',
          body: JSON.stringify({
            contactId,
            text,
            sender,
            mediaBase64: mediaOptions?.mediaBase64,
            mediaType: mediaOptions?.mediaType,
            mediaMimeType: mediaOptions?.mediaMimeType,
            mediaFileName: mediaOptions?.mediaFileName,
            isVoiceNote: mediaOptions?.isVoiceNote,
            tempId: effectiveTempId
          })
        }
      );
    },
    update: async (contactId: string, updates: Partial<CustomerContact>): Promise<CustomerContact> => {
      const res = await fetchJson<{ success: boolean; data: CustomerContact }>(
        `${API_BASE}/contacts/${contactId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates)
        }
      );
      return res.data;
    },
    markAsRead: async (contactId: string): Promise<boolean> => {
      try {
        await fetchJson<{ success: boolean }>(`${API_BASE}/contacts/${encodeURIComponent(contactId)}/read`, {
          method: 'POST'
        });
        return true;
      } catch (e) {
        console.warn('[Messages] Erreur markAsRead:', e);
        return false;
      }
    },
    syncAvatar: async (contactId: string): Promise<string | null> => {
      try {
        const res = await fetchJson<{ success: boolean; avatar: string | null }>(
          `${API_BASE}/contacts/${encodeURIComponent(contactId)}/sync-avatar`,
          { method: 'POST' }
        );
        return res.avatar || null;
      } catch (_) {
        return null;
      }
    },
    refreshSummary: async (contactId: string): Promise<{ success: boolean; notes: string }> => {
      try {
        return await fetchJson<{ success: boolean; notes: string }>(
          `${API_BASE}/contacts/${encodeURIComponent(contactId)}/refresh-summary`,
          { method: 'POST' }
        );
      } catch (err: any) {
        return { success: false, notes: '' };
      }
    },
    getSuggestReplies: async (contactId: string): Promise<{
      success: boolean;
      suggestions: string[];
      cached: boolean;
      canRegenerate: boolean;
    }> => {
      return fetchJson<{
        success: boolean;
        suggestions: string[];
        cached: boolean;
        canRegenerate: boolean;
      }>(`${API_BASE}/discussions/${encodeURIComponent(contactId)}/suggest-replies`);
    },
    generateSuggestReplies: async (contactId: string): Promise<{
      success: boolean;
      suggestions: string[];
      cached: boolean;
      canRegenerate: boolean;
    }> => {
      return fetchJson<{
        success: boolean;
        suggestions: string[];
        cached: boolean;
        canRegenerate: boolean;
      }>(`${API_BASE}/discussions/${encodeURIComponent(contactId)}/suggest-replies`, {
        method: 'POST',
        body: JSON.stringify({})
      });
    },
    syncAvatars: async (options?: { contactIds?: string[]; accountId?: string }): Promise<{ count: number }> => {
      try {
        const res = await fetchJson<{ success: boolean; count: number }>(
          `${API_BASE}/contacts/sync-avatars`,
          {
            method: 'POST',
            body: JSON.stringify(options || {})
          }
        );
        return { count: res.count || 0 };
      } catch (_) {
        return { count: 0 };
      }
    }
  },

  // --- INTERVENTIONS ---
  interventions: {
    getAll: async (params?: { category?: string; accountId?: string }): Promise<CustomerContact[]> => {
      const query = new URLSearchParams();
      if (params?.category) query.set('category', params.category);
      if (params?.accountId) query.set('accountId', params.accountId);

      const res = await fetchJson<{ success: boolean; data: CustomerContact[] }>(
        `${API_BASE}/interventions?${query.toString()}`
      );
      return res.data;
    },
    resolve: async (payload: {
      contactId: string;
      action: 'accepted' | 'refused' | 'custom_instruction' | 'quick_option' | 'dismiss' | 'dismissed' | 'takeover';
      instruction?: string;
      generatedMessage?: string;
      interventionId?: string;
      interventionReason?: string;
      interventionCategory?: string;
      orderId?: string;
      orderNumber?: string;
    }) => {
      return fetchJson<{ success: boolean; message: string; contact: CustomerContact }>(
        `${API_BASE}/interventions/resolve`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
    }
  },

  // --- AGENT CONFIG ---
  agent: {
    getConfig: async (accountId?: string): Promise<AIAgentConfig> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: AIAgentConfig }>(`${API_BASE}/agent/config${query}`);
      return res.data;
    },
    updateConfig: async (updates: Partial<AIAgentConfig>, accountId?: string): Promise<AIAgentConfig> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: AIAgentConfig }>(`${API_BASE}/agent/config${query}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return res.data;
    },
    copilotDirectives: async (data: {
      userMessage: string;
      currentDirectives?: string;
      businessMode?: string;
      role?: string;
    }): Promise<{ reply: string; suggestedDirectives: string }> => {
      const res = await fetchJson<{ success: boolean; data: { reply: string; suggestedDirectives: string } }>(
        `${API_BASE}/agent/copilot-directives`,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );
      return res.data;
    },
    copilotDirectivesStream: async (
      data: {
        userMessage: string;
        currentDirectives?: string;
        businessMode?: string;
        role?: string;
        accountName?: string;
        tone?: string;
        conversationHistory?: any[];
      },
      onDelta: (delta: string) => void,
      onDone: (fullText: string) => void,
      onError: (err: any) => void
    ): Promise<void> => {
      try {
        let response = await fetch(`${API_BASE}/agent/copilot-directives/stream`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (response.status === 401) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            response = await fetch(`${API_BASE}/agent/copilot-directives/stream`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(data)
            });
          }
        }

        if (!response.ok) {
          // Fallback transparent sur la route standard si le proxy ou le réseau bloque SSE
          const fallbackRes = await api.agent.copilotDirectives({
            userMessage: data.userMessage,
            currentDirectives: data.currentDirectives,
            businessMode: data.businessMode,
            role: data.role
          });
          const full = fallbackRes.suggestedDirectives
            ? `${fallbackRes.reply}\n\n[DIRECTIVES]\n${fallbackRes.suggestedDirectives}\n[/DIRECTIVES]`
            : fallbackRes.reply;
          onDone(full);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('Flux indisponible');

        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let fullAccumulated = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data:')) continue;
            const payload = trimmed.slice(5).trim();
            if (payload === '[DONE]') {
              onDone(fullAccumulated);
              return;
            }
            try {
              const parsed = JSON.parse(payload);
              if (parsed.delta) {
                fullAccumulated += parsed.delta;
                onDelta(parsed.delta);
              }
              if (parsed.done) {
                onDone(parsed.fullText || fullAccumulated);
                return;
              }
              if (parsed.error) {
                const err: any = new Error(parsed.error);
                if (parsed.copilotLimitReached) {
                  err.copilotLimitReached = true;
                  err.planId = parsed.planId;
                  err.planName = parsed.planName;
                  err.sessionLimit = parsed.sessionLimit;
                }
                onError(err);
                return;
              }
            } catch {}
          }
        }
        onDone(fullAccumulated);
      } catch (err: any) {
        // En cas d'exception réseau totale, tentative de secours via route POST standard
        try {
          const fallbackRes = await api.agent.copilotDirectives({
            userMessage: data.userMessage,
            currentDirectives: data.currentDirectives,
            businessMode: data.businessMode,
            role: data.role
          });
          const full = fallbackRes.suggestedDirectives
            ? `${fallbackRes.reply}\n\n[DIRECTIVES]\n${fallbackRes.suggestedDirectives}\n[/DIRECTIVES]`
            : fallbackRes.reply;
          onDone(full);
        } catch {
          onError(err);
        }
      }
    }
  },

  // --- BASE DE CONNAISSANCES ---
  knowledge: {
    getAll: async (accountId?: string): Promise<KnowledgeItem[]> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: KnowledgeItem[] }>(`${API_BASE}/knowledge${query}`);
      return res.data;
    },
    create: async (item: Partial<KnowledgeItem> & { title: string; category: string; content: string }): Promise<KnowledgeItem> => {
      const res = await fetchJson<{ success: boolean; data: KnowledgeItem }>(`${API_BASE}/knowledge`, {
        method: 'POST',
        body: JSON.stringify(item)
      });
      return res.data;
    },
    update: async (id: string, updates: Partial<KnowledgeItem>): Promise<KnowledgeItem> => {
      const res = await fetchJson<{ success: boolean; data: KnowledgeItem }>(`${API_BASE}/knowledge/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return res.data;
    },
    delete: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/knowledge/${id}`, { method: 'DELETE' });
    },
    toggle: async (id: string): Promise<KnowledgeItem> => {
      const res = await fetchJson<{ success: boolean; data: KnowledgeItem }>(`${API_BASE}/knowledge/${id}/toggle`, {
        method: 'PATCH'
      });
      return res.data;
    },
    aiExtract: async (payload: {
      rawText?: string;
      fileData?: string;
      mimeType?: string;
      businessMode?: string;
    }): Promise<{ success: boolean; count: number; items: ExtractedKnowledgeItem[]; provider?: string }> => {
      return await fetchJson<{ success: boolean; count: number; items: ExtractedKnowledgeItem[]; provider?: string }>(
        `${API_BASE}/knowledge/ai-extract`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
    },
    createBulk: async (payload: {
      items: Array<{ title: string; category: string; content: string; imageUrls?: string[]; imageUrl?: string; stockQuantity?: number; trackStock?: boolean }>;
      accountId?: string;
    }): Promise<KnowledgeItem[]> => {
      const res = await fetchJson<{ success: boolean; count: number; data: KnowledgeItem[] }>(
        `${API_BASE}/knowledge/bulk`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
      return res.data;
    },
    getPresentation: async (accountId?: string): Promise<{ item: KnowledgeItem; content: MasterPresentationContent }> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: { item: KnowledgeItem; content: MasterPresentationContent } }>(`${API_BASE}/knowledge/presentation${query}`);
      return res.data;
    },
    updatePresentation: async (payload: {
      accountId?: string;
      introPitch?: string;
      autoSyncEnabled?: boolean;
      items?: LinkedPresentationItem[];
    }): Promise<MasterPresentationContent> => {
      const res = await fetchJson<{ success: boolean; data: MasterPresentationContent }>(`${API_BASE}/knowledge/presentation`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      return res.data;
    },
    compactPresentation: async (accountId?: string): Promise<MasterPresentationContent> => {
      const res = await fetchJson<{ success: boolean; data: MasterPresentationContent }>(`${API_BASE}/knowledge/presentation/compact`, {
        method: 'POST',
        body: JSON.stringify({ accountId })
      });
      return res.data;
    }
  },

  // --- CAMPAGNES ---
  campaigns: {
    getAll: async (accountId?: string): Promise<BroadcastCampaign[]> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: BroadcastCampaign[] }>(`${API_BASE}/campaigns${query}`);
      return res.data;
    },
    create: async (campaignData: Partial<BroadcastCampaign> & { name: string; messageTemplate: string }): Promise<BroadcastCampaign> => {
      const res = await fetchJson<{ success: boolean; data: BroadcastCampaign }>(`${API_BASE}/campaigns`, {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });
      return res.data;
    },
    updateStatus: async (id: string, status: BroadcastCampaign['status']): Promise<BroadcastCampaign> => {
      const res = await fetchJson<{ success: boolean; data: BroadcastCampaign }>(`${API_BASE}/campaigns/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
      return res.data;
    },
    delete: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/campaigns/${id}`, { method: 'DELETE' });
    },
    launch: async (id: string): Promise<{ success: boolean; recipientCount: number; message: string }> => {
      const res = await fetchJson<{ success: boolean; recipientCount: number; message: string }>(`${API_BASE}/campaigns/${id}/launch`, {
        method: 'POST'
      });
      return res;
    },
    pause: async (id: string): Promise<{ success: boolean; message: string }> => {
      const res = await fetchJson<{ success: boolean; message: string }>(`${API_BASE}/campaigns/${id}/pause`, {
        method: 'POST'
      });
      return res;
    },
    resume: async (id: string): Promise<{ success: boolean; message: string }> => {
      const res = await fetchJson<{ success: boolean; message: string }>(`${API_BASE}/campaigns/${id}/resume`, {
        method: 'POST'
      });
      return res;
    },
    getStats: async (id: string): Promise<{ success: boolean; data: any }> => {
      const res = await fetchJson<{ success: boolean; data: any }>(`${API_BASE}/campaigns/${id}/stats`);
      return res;
    },
    getRecipients: async (id: string, params?: { status?: string; page?: number; limit?: number }): Promise<{ success: boolean; data: any[]; total: number; totalPages: number }> => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      const qs = query.toString() ? `?${query.toString()}` : '';
      const res = await fetchJson<{ success: boolean; data: any[]; total: number; totalPages: number }>(`${API_BASE}/campaigns/${id}/recipients${qs}`);
      return res;
    },
    getSegmentCount: async (accountId: string, segment: string): Promise<{ count: number; total?: number; maxAllowed?: number; estimate?: CampaignEstimate }> => {
      const res = await fetchJson<{ success: boolean; count: number; total?: number; maxAllowed?: number; estimate?: CampaignEstimate }>(`${API_BASE}/campaigns/segments/count?accountId=${encodeURIComponent(accountId)}&segment=${encodeURIComponent(segment)}`);
      return { count: res.count || 0, total: res.total, maxAllowed: res.maxAllowed, estimate: res.estimate };
    },
    estimateDuration: async (accountId: string, count: number, segment?: string): Promise<CampaignEstimate> => {
      const query = new URLSearchParams({
        accountId,
        count: String(count),
        ...(segment ? { segment } : {})
      });
      const res = await fetchJson<{ success: boolean; data: CampaignEstimate }>(`${API_BASE}/campaigns/estimate?${query.toString()}`);
      return res.data;
    },
    generateVariants: async (params: {
      userBrief: string;
      targetSegment?: string;
      audienceCount?: number;
      tone?: string;
      accountId?: string;
      businessMode?: string;
    }): Promise<{
      success: boolean;
      variants: Array<{ id: string; title: string; angle: string; text: string; explanation: string }>;
      recommendedIndex: number;
      provider: string;
      error?: string;
    }> => {
      return await fetchJson(`${API_BASE}/campaigns/generate-variants`, {
        method: 'POST',
        body: JSON.stringify(params)
      });
    }
  },

  // --- MÉTRIQUES ---
  activity: {
    getMetrics: async (accountId?: string): Promise<DashboardMetrics> => {
      const query = accountId ? `?accountId=${accountId}` : '';
      const res = await fetchJson<{ success: boolean; data: DashboardMetrics }>(`${API_BASE}/activity/metrics${query}`);
      return res.data;
    }
  },

  // --- APPOINTMENTS ---
  appointments: {
    getSlots: async (accountId: string, date: string): Promise<{ slots: AppointmentSlot[]; timezone: string; timezoneDisplay: string; durationMinutes: number }> => {
      const res = await fetchJson<{ success: boolean; data: { slots: AppointmentSlot[]; timezone: string; timezoneDisplay: string; durationMinutes: number } }>(
        `${API_BASE}/appointments/slots?accountId=${encodeURIComponent(accountId)}&date=${encodeURIComponent(date)}`
      );
      return res.data;
    },
    list: async (params: { accountId: string; status?: string; date?: string; limit?: number; offset?: number }): Promise<{ appointments: Appointment[]; total: number }> => {
      const queryParams = new URLSearchParams({ accountId: params.accountId });
      if (params.status) queryParams.set('status', params.status);
      if (params.date) queryParams.set('date', params.date);
      if (params.limit) queryParams.set('limit', String(params.limit));
      if (params.offset) queryParams.set('offset', String(params.offset));
      const res = await fetchJson<{ success: boolean; data: { appointments: Appointment[]; total: number } }>(
        `${API_BASE}/appointments?${queryParams.toString()}`
      );
      return res.data;
    },
    create: async (data: { accountId: string; customerPhone: string; customerName?: string; startAt: string; endAt: string; notes?: string }): Promise<Appointment> => {
      const res = await fetchJson<{ success: boolean; data: Appointment }>(`${API_BASE}/appointments`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return res.data;
    },
    confirm: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/${id}/confirm`, { method: 'PATCH' });
    },
    cancel: async (id: string, reason?: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/${id}/cancel`, {
        method: 'PATCH',
        body: JSON.stringify({ reason })
      });
    },
    complete: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/${id}/complete`, { method: 'PATCH' });
    },
    noShow: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/${id}/no-show`, { method: 'PATCH' });
    },
    reschedule: async (id: string, newStartAt: string, newEndAt: string): Promise<Appointment> => {
      const res = await fetchJson<{ success: boolean; data: Appointment }>(`${API_BASE}/appointments/${id}/reschedule`, {
        method: 'PATCH',
        body: JSON.stringify({ newStartAt, newEndAt })
      });
      return res.data;
    },
    getSchedule: async (accountId: string): Promise<{ schedules: BusinessSchedule[]; holidays: BusinessHoliday[]; config: AppointmentConfig; timezone: string }> => {
      const res = await fetchJson<{ success: boolean; data: { schedules: BusinessSchedule[]; holidays: BusinessHoliday[]; config: AppointmentConfig; timezone: string } }>(
        `${API_BASE}/appointments/schedule?accountId=${encodeURIComponent(accountId)}`
      );
      return res.data;
    },
    saveSchedule: async (accountId: string, data: { schedules?: Partial<BusinessSchedule>[]; config?: Partial<AppointmentConfig> }): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/schedule`, {
        method: 'PUT',
        body: JSON.stringify({ accountId, ...data })
      });
    },
    addHoliday: async (accountId: string, date: string, reason?: string): Promise<BusinessHoliday> => {
      const res = await fetchJson<{ success: boolean; data: BusinessHoliday }>(`${API_BASE}/appointments/holidays`, {
        method: 'POST',
        body: JSON.stringify({ accountId, date, reason })
      });
      return res.data;
    },
    removeHoliday: async (id: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/holidays/${id}`, { method: 'DELETE' });
    },
    getGoogleCalendarStatus: async (accountId: string): Promise<{ connected: boolean; email?: string; name?: string; calendarId?: string; syncEnabled?: boolean; autoBlock?: boolean; lastSyncedAt?: string }> => {
      const res = await fetchJson<{ success: boolean; data: any }>(
        `${API_BASE}/appointments/google-calendar/status?accountId=${encodeURIComponent(accountId)}`
      );
      return res.data;
    },
    connectGoogleCalendar: async (data: {
      accountId: string;
      accessToken?: string;
      email?: string;
      name?: string;
      calendarId?: string;
      syncEnabled?: boolean;
      autoBlock?: boolean;
    }): Promise<any> => {
      const res = await fetchJson<{ success: boolean; data: any }>(
        `${API_BASE}/appointments/google-calendar/connect`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      return res.data;
    },
    updateGoogleCalendarSettings: async (data: {
      accountId: string;
      syncEnabled?: boolean;
      autoBlock?: boolean;
    }): Promise<any> => {
      const res = await fetchJson<{ success: boolean; data: any }>(
        `${API_BASE}/appointments/google-calendar/settings`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      return res.data;
    },
    disconnectGoogleCalendar: async (accountId: string): Promise<void> => {
      await fetchJson(`${API_BASE}/appointments/google-calendar/disconnect`, {
        method: 'POST',
        body: JSON.stringify({ accountId }),
      });
    },
    syncGoogleCalendar: async (accountId: string): Promise<{ syncedCount: number; message: string }> => {
      const res = await fetchJson<{ success: boolean; data: { syncedCount: number; message: string } }>(
        `${API_BASE}/appointments/google-calendar/sync-now`,
        {
          method: 'POST',
          body: JSON.stringify({ accountId }),
        }
      );
      return res.data;
    }
  },

  // --- AUTH & PROFIL ---
  auth: {
    getMe: async (): Promise<any> => {
      const res = await fetchJson<{ success: boolean; user: any }>(`${API_BASE}/auth/me`);
      return res.user;
    },
    updateProfile: async (data: { fullName?: string; phone?: string; businessName?: string; email?: string }): Promise<any> => {
      const res = await fetchJson<{ success: boolean; user: any }>(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      return res.user;
    },
    updatePassword: async (data: { currentPassword: string; newPassword: string }): Promise<{ success: boolean; message: string }> => {
      return fetchJson<{ success: boolean; message: string }>(`${API_BASE}/auth/password`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },
    toggleViralLoop: async (enabled: boolean): Promise<{ success: boolean; viralLoopEnabled: boolean; lockedUntil?: string; message?: string }> => {
      return fetchJson<{ success: boolean; viralLoopEnabled: boolean; lockedUntil?: string; message?: string }>(`${API_BASE}/auth/viral-loop`, {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      });
    }
  },

  // --- TEMPS RÉEL (SOCKET.IO) ---
  realtime: (() => {
    let currentSocket: Socket | null = null;
    let currentAccountId: string | null = null;

    const listeners = {
      message: [] as Array<(data: any) => void>,
      contactUpdate: [] as Array<(data: any) => void>,
      contactsSynced: [] as Array<(data: any) => void>,
      historySynced: [] as Array<(data: any) => void>,
      connectionStatus: [] as Array<(data: any) => void>,
      presenceUpdate: [] as Array<(data: { phone: string; jid: string; status: string; isOnline: boolean; lastSeen: number | null }) => void>,
      circuitBreaker: [] as Array<(data: { account_id: string; reason: string; pauseMinutes: number; pausedUntil: string; consecutiveErrors: number }) => void>,
      messageStatusUpdate: [] as Array<(data: { contact_id: string; account_id?: string; message_id: string; old_message_id?: string; proxy_message_id?: string; status: 'sent' | 'delivered' | 'read' | 'failed' }) => void>,
      messageIdUpdated: [] as Array<(data: { contact_id: string; account_id?: string; old_message_id: string; message_id: string }) => void>,
      messageReactionUpdated: [] as Array<(data: { contact_id: string; account_id?: string; message_id: string; reactions: any[] }) => void>,
      discussionRead: [] as Array<(data: { contact_id: string; account_id?: string }) => void>,
      typing: [] as Array<(data: { contactId: string; userId: string; isTyping: boolean }) => void>,
      operationsUpdate: [] as Array<(data: any) => void>,
      quotaUpdated: [] as Array<(data: { quota_used: number; quota_total: number; account_id?: string }) => void>,
      campaignUpdated: [] as Array<(data: { campaign_id: string; status: string; account_id?: string }) => void>,
      contactsChanged: [] as Array<(data: { account_id?: string }) => void>,
    };

    function getSocketBaseUrl(): string {
      return getBackendOrigin();
    }

    function dispatchChannel(channel: string, payload: any) {
      if (!payload) return;
      if (channel === 'new_message') {
        listeners.message.forEach((fn) => fn(payload));
      } else if (channel === 'contact_updated') {
        listeners.contactUpdate.forEach((fn) => fn(payload));
      } else if (channel === 'contacts_synced') {
        listeners.contactsSynced.forEach((fn) => fn(payload));
      } else if (channel === 'history_synced') {
        listeners.historySynced.forEach((fn) => fn(payload));
      } else if (channel === 'connection_status') {
        listeners.connectionStatus.forEach((fn) => fn(payload));
      } else if (channel === 'presence_update') {
        listeners.presenceUpdate.forEach((fn) => fn(payload));
      } else if (channel === 'circuit_breaker') {
        listeners.circuitBreaker.forEach((fn) => fn(payload));
      } else if (channel === 'message_status_update') {
        listeners.messageStatusUpdate.forEach((fn) => fn(payload));
      } else if (channel === 'message_id_updated') {
        listeners.messageIdUpdated.forEach((fn) => fn(payload));
      } else if (channel === 'message_reaction_updated') {
        listeners.messageReactionUpdated.forEach((fn) => fn(payload));
      } else if (channel === 'discussion_read') {
        listeners.discussionRead.forEach((fn) => fn(payload));
      } else if (
        channel === 'order_created' ||
        channel === 'order_updated' ||
        channel === 'delivery_created' ||
        channel === 'delivery_updated' ||
        channel === 'report_created' ||
        channel === 'report_updated' ||
        channel === 'appointment_created' ||
        channel === 'appointment_updated'
      ) {
        listeners.operationsUpdate.forEach((fn) => fn({ channel, ...payload }));
      } else if (channel === 'quota_updated') {
        listeners.quotaUpdated.forEach((fn) => fn(payload));
      } else if (channel === 'campaign_updated') {
        listeners.campaignUpdated.forEach((fn) => fn(payload));
      } else if (channel === 'contacts_changed') {
        listeners.contactsChanged.forEach((fn) => fn(payload));
      }
    }

    function createSocket(accountId: string): Socket {
      currentAccountId = accountId;

      // Cookie httpOnly transmis automatiquement (proxy Vite /socket.io → same-origin)
      const socket = io(getSocketBaseUrl(), {
        path: '/socket.io',
        withCredentials: true,
        query: { accountId },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 3000,
        timeout: 10000,
      });

      socket.on('connect', () => {
        // Ref. courant (et non le paramètre figé) : après une bascule de compte,
        // une reconnexion doit rejoindre le compte ACTIF.
        if (currentAccountId) {
          socket.emit('join:account', currentAccountId);
        }
      });

      // Bus unique : le backend diffuse TOUT via 'realtime_event' (emitSocketEvent émet
      // à la fois le canal nommé ET l'événement universel). Écouter les deux provoquait
      // un double traitement de chaque événement. Seuls les événements de relais bruts
      // (typing:*) ne passent pas par emitSocketEvent et restent en écoute directe.
      socket.on('realtime_event', (event: { channel: string; payload: any }) => {
        if (event?.channel && event?.payload) {
          dispatchChannel(event.channel, event.payload);
        }
      });

      let isRefreshingSocket = false;
      socket.on('connect_error', async (err: any) => {
        const msg = String(err?.message || '');
        if (
          msg.includes('token') ||
          msg.includes('Authentification') ||
          msg.includes('expiré') ||
          msg.includes('invalide') ||
          msg.includes('refusée')
        ) {
          if (!isRefreshingSocket) {
            isRefreshingSocket = true;
            try {
              const newToken = await refreshAccessToken();
              if (newToken && socket.disconnected) {
                socket.connect();
              }
            } catch {
              // ignore
            } finally {
              isRefreshingSocket = false;
            }
          }
        }
      });

      socket.on('disconnect', (reason) => {
        if (reason === 'io server disconnect') {
          setTimeout(() => {
            if (socket.disconnected) {
              refreshAccessToken()
                .then(() => socket.connect())
                .catch(() => socket.connect());
            }
          }, 1000);
        }
      });

      // Typing indicators temps réel
      socket.on('typing:start', (data: { contactId: string; userId: string }) => {
        listeners.typing.forEach((fn) => fn({ contactId: data.contactId, userId: data.userId, isTyping: true }));
      });
      socket.on('typing:stop', (data: { contactId: string; userId: string }) => {
        listeners.typing.forEach((fn) => fn({ contactId: data.contactId, userId: data.userId, isTyping: false }));
      });

      return socket;
    }

    return {
      isConnected: () => Boolean(currentSocket?.connected),
      connect: (accountId: string) => {
        // Socket unique et réutilisée. Recréer le socket à chaque bascule (ancien code)
        // détruisait une connexion en cours d'établissement et provoquait une boucle
        // connect/disconnect tant que currentSocket.connected était faux, ce qui
        // coupait le temps réel pendant toute la négociation.
        if (!currentSocket) {
          currentSocket = createSocket(accountId);
          return;
        }
        // Même compte : socket.io assure déjà la reconnexion, on laisse tourner.
        if (currentAccountId === accountId) return;

        currentAccountId = accountId;
        // Les rooms de TOUS les comptes de l'utilisateur sont jointes côté serveur au
        // moment de l'authentification (socket.ts) et la vue unifiée en a besoin :
        // on ne quitte jamais de room, on garantit seulement l'adhésion au compte ciblé
        // (utile après invalidation du cache serveur : ajout d'un compte/membre en direct).
        currentSocket.emit('join:account', accountId);
      },

      disconnect: () => {
        if (currentSocket) {
          currentSocket.disconnect();
          currentSocket = null;
          currentAccountId = null;
        }
      },

      onMessage: (fn: (data: any) => void) => {
        listeners.message.push(fn);
        return () => { listeners.message = listeners.message.filter((f) => f !== fn); };
      },

      onContactUpdate: (fn: (data: any) => void) => {
        listeners.contactUpdate.push(fn);
        return () => { listeners.contactUpdate = listeners.contactUpdate.filter((f) => f !== fn); };
      },

      onContactsSynced: (fn: (data: any) => void) => {
        listeners.contactsSynced.push(fn);
        return () => { listeners.contactsSynced = listeners.contactsSynced.filter((f) => f !== fn); };
      },

      onHistorySynced: (fn: (data: any) => void) => {
        listeners.historySynced.push(fn);
        return () => { listeners.historySynced = listeners.historySynced.filter((f) => f !== fn); };
      },

      onConnectionStatus: (fn: (data: any) => void) => {
        listeners.connectionStatus.push(fn);
        return () => { listeners.connectionStatus = listeners.connectionStatus.filter((f) => f !== fn); };
      },

      onPresenceUpdate: (fn: (data: { phone: string; jid: string; status: string; isOnline: boolean; lastSeen: number | null }) => void) => {
        listeners.presenceUpdate.push(fn);
        return () => { listeners.presenceUpdate = listeners.presenceUpdate.filter((f) => f !== fn); };
      },

      onCircuitBreaker: (fn: (data: { account_id: string; reason: string; pauseMinutes: number; pausedUntil: string; consecutiveErrors: number }) => void) => {
        listeners.circuitBreaker.push(fn);
        return () => { listeners.circuitBreaker = listeners.circuitBreaker.filter((f) => f !== fn); };
      },

      onMessageStatusUpdate: (fn: (data: { contact_id: string; account_id?: string; message_id: string; old_message_id?: string; proxy_message_id?: string; status: 'sent' | 'delivered' | 'read' | 'failed' }) => void) => {
        listeners.messageStatusUpdate.push(fn);
        return () => { listeners.messageStatusUpdate = listeners.messageStatusUpdate.filter((f) => f !== fn); };
      },

      onMessageIdUpdated: (fn: (data: { contact_id: string; account_id?: string; old_message_id: string; message_id: string }) => void) => {
        listeners.messageIdUpdated.push(fn);
        return () => { listeners.messageIdUpdated = listeners.messageIdUpdated.filter((f) => f !== fn); };
      },

      onMessageReactionUpdated: (fn: (data: { contact_id: string; account_id?: string; message_id: string; reactions: any[] }) => void) => {
        listeners.messageReactionUpdated.push(fn);
        return () => { listeners.messageReactionUpdated = listeners.messageReactionUpdated.filter((f) => f !== fn); };
      },

      onDiscussionRead: (fn: (data: { contact_id: string; account_id?: string }) => void) => {
        listeners.discussionRead.push(fn);
        return () => { listeners.discussionRead = listeners.discussionRead.filter((f) => f !== fn); };
      },

      onOperationsUpdate: (fn: (data: any) => void) => {
        listeners.operationsUpdate.push(fn);
        return () => { listeners.operationsUpdate = listeners.operationsUpdate.filter((f) => f !== fn); };
      },

      onQuotaUpdated: (fn: (data: { quota_used: number; quota_total: number; account_id?: string }) => void) => {
        listeners.quotaUpdated.push(fn);
        return () => { listeners.quotaUpdated = listeners.quotaUpdated.filter((f) => f !== fn); };
      },

      onCampaignUpdated: (fn: (data: { campaign_id: string; status: string; account_id?: string }) => void) => {
        listeners.campaignUpdated.push(fn);
        return () => { listeners.campaignUpdated = listeners.campaignUpdated.filter((f) => f !== fn); };
      },

      onContactsChanged: (fn: (data: { account_id?: string }) => void) => {
        listeners.contactsChanged.push(fn);
        return () => { listeners.contactsChanged = listeners.contactsChanged.filter((f) => f !== fn); };
      },
    };
  })(),

  // ============================================================================
  // OPERATIONS (Commandes, Livraisons, RDV, Signalements)
  // ============================================================================
  operations: {
    /** Récupère la vue unifiée avec les types disponibles (filtres dynamiques) */
    list: async (params: {
      accountId?: string;
      type?: OperationType;
      status?: string;
      limit?: number;
      offset?: number;
    }): Promise<OperationsData> => {
      const q = new URLSearchParams();
      if (params.accountId) q.set('accountId', params.accountId);
      if (params.type) q.set('type', params.type);
      if (params.status) q.set('status', params.status);
      if (params.limit !== undefined) q.set('limit', String(params.limit));
      if (params.offset !== undefined) q.set('offset', String(params.offset));
      const res = await fetchJson<{ success: boolean; data: OperationsData }>(`${API_BASE}/operations?${q.toString()}`);
      return res.data;
    },

    /** Met à jour le statut d'une commande */
    updateOrder: async (id: string, update: { status?: OrderStatus; delivery_address?: string; delivery_notes?: string; payment_status?: string; deposit_amount?: number }): Promise<OperationOrder> => {
      const res = await fetchJson<{ success: boolean; data: OperationOrder }>(`${API_BASE}/operations/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(update)
      });
      return res.data;
    },

    /** Met à jour le statut d'une livraison */
    updateDelivery: async (id: string, update: { status?: DeliveryStatus; tracking_number?: string; carrier?: string; estimated_delivery_date?: string; notes?: string }): Promise<OperationDelivery> => {
      const res = await fetchJson<{ success: boolean; data: OperationDelivery }>(`${API_BASE}/operations/deliveries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(update)
      });
      return res.data;
    },

    /** Crée une livraison liée à une commande */
    createDelivery: async (data: { account_id: string; contact_id?: string; order_id?: string; order_number?: string; delivery_address?: string; carrier?: string; tracking_number?: string; estimated_delivery_date?: string; notes?: string }): Promise<OperationDelivery> => {
      const res = await fetchJson<{ success: boolean; data: OperationDelivery }>(`${API_BASE}/operations/deliveries`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return res.data;
    },

    /** Met à jour le statut d'un signalement */
    updateReport: async (id: string, update: { status?: ReportStatus; priority?: ReportPriority; resolution?: string }): Promise<OperationReport> => {
      const res = await fetchJson<{ success: boolean; data: OperationReport }>(`${API_BASE}/operations/reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(update)
      });
      return res.data;
    },

    /** Crée un signalement */
    createReport: async (data: { account_id: string; contact_id?: string; order_id?: string; type?: ReportType; priority?: ReportPriority; subject: string; description?: string }): Promise<OperationReport> => {
      const res = await fetchJson<{ success: boolean; data: OperationReport }>(`${API_BASE}/operations/reports`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return res.data;
    }
  },

  // --- FACTURATION & QUOTAS ---
  billing: {
    getQuota: async (signal?: AbortSignal) => {
      return fetchJson<{
        success: boolean;
        data: {
          quota_total: number;
          quota_used: number;
          remaining: number;
          plan_id: string;
          plan_name: string;
          activated_at: string;
          current_period_end: string | null;
          current_period_start: string | null;
          billing_cycle: string;
          status: string;
          days_remaining: number | null;
          is_expired: boolean;
          is_quota_exceeded: boolean;
          is_free_quota_exhausted: boolean;
          quota_exhausted_at: string | null;
          is_free_quota_grace_active: boolean;
          free_quota_grace_remaining_ms: number;
          free_quota_grace_expired: boolean;
        };
      }>(`${API_BASE}/billing/quota`, { signal });
    },
    getEntitlements: async (signal?: AbortSignal) => {
      return fetchJson<{
        success: boolean;
        data: {
          planId: string;
          planName: string;
          isExpired: boolean;
          extraWhatsappAccounts: number;
          addonMessagesRemaining: number;
          canAddExtraWhatsapp: boolean;
          limits: Record<string, number>;
          features: Record<string, boolean>;
          quotas: {
            whatsappAccounts: { current: number; limit: number; remaining: number; allowed: boolean };
            knowledgeDocs: { current: number; limit: number; remaining: number; allowed: boolean };
            aiExtractWeekly?: { current: number; limit: number; remaining: number; allowed?: boolean };
            messages: { current: number; limit: number; remaining: number };
          };
        };
      }>(`${API_BASE}/billing/entitlements`, { signal });
    }
  },

  // --- PARTENARIATS CRÉATEURS ---
  partnership: {
    apply: async (data: { name: string; email: string; phone?: string; channelLinks: string; message?: string }) => {
      return fetchJson<{ success: boolean; message: string; data?: any }>(`${API_BASE}/partnership/apply`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    getDashboard: async () => {
      return fetchJson<{
        success: boolean;
        partner: {
          code: string;
          link: string;
          status: string;
          name: string;
          email: string;
          phone?: string;
          reviewedAt: string;
          proGranted: boolean;
          proExpiresAt?: string;
          commissionRate: number;
          payoutDelayDays: number;
        };
        metrics: {
          totalClicks: number;
          totalSignups: number;
          totalSubscribers: number;
          totalPaying: number;
          totalRevenueXof: number;
          totalCommissionXof: number;
          availableCommissionXof: number;
          pendingCommissionXof: number;
          paidOutCommissionXof: number;
          payoutDelayDays: number;
          commissionRate: number;
          conversionRate: number;
        };
        referrals: Array<{
          id: string;
          name: string;
          maskedEmail: string;
          plan: string;
          isPaid: boolean;
          amountPaidXof: number;
          commissionXof: number;
          status: string;
          createdAt: string;
          paidAt?: string | null;
          isAvailableForPayout: boolean;
          daysUntilAvailable: number;
        }>;
        payoutRequests: Array<{
          id: string;
          amount: number;
          payment_method: string;
          account_identifier: string;
          account_name?: string;
          status: string;
          created_at: string;
        }>;
        recentClicks: Array<{
          id: string;
          referer?: string;
          created_at: string;
        }>;
      }>(`${API_BASE}/partnership/dashboard`);
    },

    requestPayout: async (data: {
      amount: number;
      paymentMethod?: string;
      accountIdentifier: string;
      accountName?: string;
      pin: string;
    }) => {
      return fetchJson<{
        success: boolean;
        message: string;
        payoutRequest?: any;
        error?: string;
      }>(`${API_BASE}/partnership/request-payout`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    getPayoutPinStatus: async () => {
      return fetchJson<{ success: boolean; configured: boolean; setAt?: string | null }>(`${API_BASE}/partnership/payout-pin/status`);
    },

    setPayoutPin: async (data: { pin: string; confirmPin: string; currentPin?: string }) => {
      return fetchJson<{ success: boolean; message?: string; error?: string }>(`${API_BASE}/partnership/payout-pin`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    requestPayoutPinReset: async () => {
      return fetchJson<{ success: boolean; message: string; maskedEmail: string; expiresInMinutes: number }>(`${API_BASE}/partnership/payout-pin/request-reset`, {
        method: 'POST'
      });
    },

    resetPayoutPinWithOtp: async (data: { otp: string; pin: string; confirmPin: string }) => {
      return fetchJson<{ success: boolean; message: string; error?: string }>(`${API_BASE}/partnership/payout-pin/reset-with-otp`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    trackClick: async (partnerCode: string, referer?: string) => {
      return fetchJson<{ success: boolean; message: string }>(`${API_BASE}/partnership/track-click`, {
        method: 'POST',
        body: JSON.stringify({ partnerCode, referer })
      });
    }
  }
};
